import React, { useState } from 'react';
import { ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { Badge, Button, Separator } from '@/shared/components';
import { formatDateShort } from '@/app/utils/formatDate';
import type { MotTest, MotDefect } from '../types/motHistory.types';
import { deriveMotTestBadge } from '../utils/motTestStatus';

interface MotTestRowProps {
  test: MotTest;
}

export const MotTestRow: React.FC<MotTestRowProps> = ({ test }) => {
  const [expanded, setExpanded] = useState(false);

  const badge = deriveMotTestBadge(test);

  const odometer =
    test.odometer_value !== null
      ? `${test.odometer_value.toLocaleString()} ${test.odometer_unit ?? 'mi'}`
      : 'No odometer reading';

  const defectsByType = test.defects.reduce<Record<string, MotDefect[]>>((acc, d) => {
    const key = d.type;
    if (!acc[key]) acc[key] = [];
    acc[key].push(d);
    return acc;
  }, {});

  return (
    <div className="border border-mp-border rounded-lg overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 bg-mp-surface">
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-mp-foreground">{formatDateShort(test.completed_date)}</span>
          <Badge variant={badge.variant}>{badge.label}</Badge>
          <span className="text-sm text-mp-muted">{odometer}</span>
        </div>
        <Button
          variant="clear"
          size="none"
          onClick={() => setExpanded((v) => !v)}
          ariaLabel={expanded ? 'Collapse test details' : 'Expand test details'}
        >
          {expanded ? <ChevronUpIcon size={16} /> : <ChevronDownIcon size={16} />}
        </Button>
      </div>

      {expanded && (
        <div className="px-4 pb-4 bg-mp-background space-y-3">
          <Separator />
          <div className="grid grid-cols-2 gap-2 text-sm">
            {test.expiry_date && (
              <>
                <span className="text-mp-muted">Expiry date:</span>
                <span className="text-mp-foreground">{formatDateShort(test.expiry_date)}</span>
              </>
            )}
            {test.mot_test_number && (
              <>
                <span className="text-mp-muted">Test number:</span>
                <span className="text-mp-foreground font-mono">{test.mot_test_number}</span>
              </>
            )}
          </div>

          {Object.entries(defectsByType).map(([type, defects]) => (
            <div key={type}>
              <p className="text-xs font-semibold text-mp-foreground uppercase tracking-wide mb-1">
                {type}
              </p>
              <ul className="space-y-1">
                {defects.map((d, i) => (
                  <li key={i} className="text-sm text-mp-foreground flex items-start gap-1">
                    {d.dangerous && <span className="text-mp-error font-bold shrink-0">!</span>}
                    <span>{d.text}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          {test.defects.length === 0 && (
            <p className="text-sm text-mp-muted italic">No defects recorded</p>
          )}
        </div>
      )}
    </div>
  );
};
